import { FAQ_ITEMS } from "@/lib/landing/faq";
import { pick } from "@/lib/landing/locale";
import { allFeatures, FEATURES, PLANS, vatPayer } from "@/lib/pricing/plans";
import type { SeoLocale, SeoPageKey } from "./seoTypes";
import { FREE_TOOL_KEYS, getGuide, GUIDE_KEYS } from "@/lib/guides";
import { isGuidePageKey, isHubPageKey } from "@/lib/site/publicPages";

/**
 * Adapter between the landing page's content and everything SEO builds from it
 * (JSON-LD offers and FAQPage, llms.txt, llms-full.txt). The landing page owns
 * the content (`src/lib/pricing/plans.ts`, `src/lib/landing/faq.ts`); this file
 * only maps it into the shapes below.
 *
 * Prices are final (the seller is not a VAT payer), so llms files and the FAQ
 * quote them. Plans stay `draft` while nothing is purchasable, and a draft plan
 * gets no schema.org Offer: an Offer says "buy this now".
 */

export interface SeoPlan {
  /** Stable id, e.g. "start-P1M". */
  id: string;
  /** Plan name as shown on the page, with the billing period, localized. */
  name: string;
  /** Price per billing period in whole currency units. */
  price: number;
  currency: "CZK" | "EUR";
  /** ISO 8601 duration of one billing period: monthly billing only. */
  billingPeriod: "P1M";
  /** One plain sentence about who the plan is for, localized. */
  description?: string;
}

export interface SeoFaqItem {
  question: string;
  /** Plain text: the same words the page shows, without markup. */
  answer: string;
}

type Localized = Record<SeoLocale, string>;

/** The fields SEO reads from `PLANS` in `src/lib/pricing/plans.ts`. */
export interface LandingPlanLike {
  id: string;
  name: string;
  /** Unapproved price: never published in structured data. */
  draft: boolean;
  /** CZK per month, the final price (IDEx Corporate s.r.o. is not a VAT payer). */
  priceMonthlyCzk: number;
  audience: Localized;
}

/** The fields SEO reads from `FAQ_ITEMS` in `src/lib/landing/faq.ts`. */
export interface LandingFaqLike {
  id: string;
  question: Localized;
  answer: Localized;
}

const MONTHLY: Record<SeoLocale, string> = { cs: "měsíčně", en: "monthly" };

/** Approved plans only (`draft === false`), each as one monthly offer. */
export function mapLandingPlans(plans: readonly LandingPlanLike[], lang: SeoLocale): SeoPlan[] {
  return plans
    .filter((plan) => plan.draft === false)
    .map((plan) => ({
      id: `${plan.id}-P1M`,
      name: `${plan.name} (${MONTHLY[lang]})`,
      price: plan.priceMonthlyCzk,
      currency: "CZK" as const,
      billingPeriod: "P1M" as const,
      description: plan.audience[lang],
    }));
}

/**
 * FAQ entries in one language. Pass the page's own picker (`pick` from
 * `@/lib/landing/locale`) so the JSON-LD text matches the visible text exactly,
 * Czech no-break spaces included.
 */
export function mapLandingFaq(
  items: readonly LandingFaqLike[],
  lang: SeoLocale,
  pick: (value: Localized, lang: SeoLocale) => string = (value, l) => value[l]
): SeoFaqItem[] {
  return items.map((item) => ({
    question: pick(item.question, lang),
    answer: pick(item.answer, lang),
  }));
}

export function getSeoPlans(lang: SeoLocale): SeoPlan[] {
  return mapLandingPlans(PLANS, lang);
}

export function getSeoFaq(lang: SeoLocale): SeoFaqItem[] {
  return mapLandingFaq(FAQ_ITEMS, lang, pick);
}

/**
 * The FAQ a page shows, for its FAQPage JSON-LD: the landing FAQ, a guide's
 * own questions, or none.
 */
export function getPageFaq(pageKey: SeoPageKey, lang: SeoLocale): SeoFaqItem[] {
  if (pageKey === "landing") return getSeoFaq(lang);
  if (isGuidePageKey(pageKey) && !isHubPageKey(pageKey)) {
    return mapLandingFaq(getGuide(pageKey).faq, lang, pick);
  }
  return [];
}

/**
 * The day a page's content last changed (YYYY-MM-DD), for the sitemap's
 * lastmod and the page's dateModified: a guide's own `updated`, a hub's
 * newest listed page. `null` where no true date is kept (the landing page,
 * legal pages), so nothing is guessed.
 */
export function getPageUpdated(pageKey: SeoPageKey): string | null {
  if (!isGuidePageKey(pageKey)) return null;
  if (isHubPageKey(pageKey)) {
    const keys = pageKey === "tools" ? FREE_TOOL_KEYS : GUIDE_KEYS;
    const dates = keys.map((key) => getGuide(key).updated).filter((d): d is string => !!d);
    return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
  }
  return getGuide(pageKey).updated ?? null;
}

/** A source a page states it checked its facts against (shown under its notes). */
export interface SeoCitation {
  name: string;
  url: string;
}

/** The public sources a guide lists under its notes, for the WebPage's citation. */
export function getPageCitations(pageKey: SeoPageKey): SeoCitation[] {
  if (!isGuidePageKey(pageKey) || isHubPageKey(pageKey)) return [];
  return (getGuide(pageKey).sources?.links ?? []).map((l) => ({ name: l.label, url: l.url }));
}

/** A free browser-only tool page, for its WebApplication and HowTo JSON-LD. */
export interface SeoTool {
  /** The tool's name as the page shows it. */
  name: string;
  /** Its one-line summary (the tools hub's line). */
  description: string;
  /** The page's H1: the HowTo's name. */
  howToName: string;
  /** The visible steps, word for word. */
  steps: { name: string; text: string }[];
}

/** `null` for every page that is not a free tool. */
export function getPageTool(pageKey: SeoPageKey, lang: SeoLocale): SeoTool | null {
  if (!isGuidePageKey(pageKey) || isHubPageKey(pageKey)) return null;
  const guide = getGuide(pageKey);
  if (!guide.freeTool) return null;
  return {
    name: pick(guide.name, lang),
    description: pick(guide.freeTool.summary, lang),
    howToName: pick(guide.h1, lang),
    steps: guide.steps.map((step) => ({
      name: pick(step.title, lang),
      text: pick(step.text, lang),
    })),
  };
}

/** A plan as llms-full.txt describes it: final prices, who it is for, what it includes. */
export interface SeoPricing {
  name: string;
  monthlyCzk: number;
  audience: string;
  /** Free days once payments launch; null = no trial promised. */
  trialDays: number | null;
  features: string[];
}

export function getSeoPricing(lang: SeoLocale): SeoPricing[] {
  return PLANS.map((plan) => ({
    name: plan.name,
    monthlyCzk: plan.priceMonthlyCzk,
    audience: pick(plan.audience, lang),
    trialDays: plan.trialDays,
    features: allFeatures(plan).map((id) => pick(FEATURES[id].label, lang)),
  }));
}

/** Whether the seller charges VAT on top (plans.ts). */
export const SELLER_IS_VAT_PAYER: boolean = vatPayer;
